/**
 * Who the SPA is signed in as: what its own token says (always available),
 * and what the backend's `GET /me` says (the cloud; a node does not serve it).
 */
import type { MeResult } from '../api/identity.js';
import { Badge } from './ui/badge.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { UNKNOWN_HINT } from '../lib/openEnum.js';

export type WhoamiCardProps = {
  readonly identity: { readonly userId: string; readonly email: string | null; readonly tenantId: string | null; readonly provider: 'cognito' | 'dev' | 'local'; readonly expiresAtMs: number; readonly local?: { readonly expiresAtMs: number } | null | undefined };
  readonly backend: { readonly kind: 'cloud' | 'node'; readonly label: string; readonly apiUrl: string };
  /** `/me`: null while loading. */
  readonly me: MeResult | null;
  readonly meError: string | null;
};

/** An ISO timestamp, or a note when the token's `exp` is outside what a Date can hold (a malformed token must not break the page). */
export function formatExpiry(ms: number): string {
  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? `invalid (${String(ms)})` : date.toISOString();
}

function Row({ label, value, testId }: { readonly label: string; readonly value: string; readonly testId: string }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="col-span-2 break-all font-mono" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

export function WhoamiCard({ identity, backend, me, meError }: WhoamiCardProps) {
  return (
    <div className="grid max-w-3xl gap-6" data-testid="whoami">
      <Card>
        <CardHeader>
          <CardTitle>Signed in</CardTitle>
          <CardDescription>From this browser’s token (unverified here — the backend verifies every request).</CardDescription>
        </CardHeader>
        <CardContent>
          <dl>
            <Row label="User" value={identity.userId} testId="whoami-user" />
            <Row label="Email" value={identity.email ?? '—'} testId="whoami-email" />
            <Row label="Tenant" value={identity.tenantId ?? '—'} testId="whoami-tenant" />
            <Row label="Sign-in" value={identity.provider === 'dev' ? 'development token' : identity.provider === 'local' ? 'local session (this site)' : 'Cognito'} testId="whoami-provider" />
            <Row label="Token expires" value={formatExpiry(identity.expiresAtMs)} testId="whoami-expires" />
            {identity.local === undefined || identity.local === null ? null : (
              <Row label="Node session" value={`local session (this site), until ${formatExpiry(identity.local.expiresAtMs)}`} testId="whoami-local" />
            )}
            <Row label="Backend" value={`${backend.kind} · ${backend.label} · ${backend.apiUrl}`} testId="whoami-backend" />
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>What the backend says</CardTitle>
          <CardDescription>GET /me</CardDescription>
        </CardHeader>
        <CardContent data-testid="whoami-me">
          {meError !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {meError}
            </p>
          ) : me === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : me.available ? (
            <div className="grid gap-3">
              <dl>
                <Row label="Tenant name" value={me.me.tenant.name} testId="me-tenant-name" />
                <Row label="Email" value={me.me.user.email} testId="me-email" />
              </dl>
              <div className="flex flex-wrap gap-1" data-testid="me-permissions">
                {/* Permissions from a newer release all read `unknown` (D37): shown once, with the hint. */}
                {[...new Set(me.me.permissions)].map((p) => (
                  <Badge key={p} variant="secondary" title={p === 'unknown' ? UNKNOWN_HINT : undefined}>
                    {p}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{me.reason}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
